import React, { useEffect, useState } from 'react';
import { Modal } from './Modal';
import { Button } from '../atoms';
import { FormField } from '../molecues/FormField';
import { Checkbox } from '../atoms';
import type {
  Trailer,
  TrailerCreateRequest,
  TrailerUpdateRequest,
} from '../../types/api/trailer';
import type {
  BannerLinkType,
  TargetAudience,
} from '../../types/api/banner';
import {
  BANNER_LINK_TYPES,
  TARGET_AUDIENCES,
} from '../../types/api/banner';

export interface TrailerFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (
    data: TrailerCreateRequest | TrailerUpdateRequest,
    videoFile?: File | null,
    thumbnailFile?: File | null
  ) => void | Promise<void>;
  mode: 'create' | 'edit';
  trailer?: Trailer;
}

type TrailerFormState = {
  title: string;
  description: string;
  altText: string;
  linkType: BannerLinkType;
  linkUrl: string;
  targetContentId: string;
  targetPlanId: string;
  targetGenreId: string;
  isActive: boolean;
  isFeatured: boolean;
  displayOrder: number;
  startDate: string;
  endDate: string;
  targetAudience: TargetAudience;
};

const createDefaultState = (): TrailerFormState => ({
  title: '',
  description: '',
  altText: '',
  linkType: 'NONE',
  linkUrl: '',
  targetContentId: '',
  targetPlanId: '',
  targetGenreId: '',
  isActive: true,
  isFeatured: false,
  displayOrder: 0,
  startDate: '',
  endDate: '',
  targetAudience: 'ALL',
});

export const TrailerFormModal: React.FC<TrailerFormModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  mode,
  trailer,
}) => {
  const [formData, setFormData] = useState<TrailerFormState>(createDefaultState());
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [videoPreview, setVideoPreview] = useState<string | null>(null);
  const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Populate form when editing
  useEffect(() => {
    if (trailer && mode === 'edit' && isOpen) {
      setFormData({
        title: trailer.title || '',
        description: trailer.description || '',
        altText: trailer.altText || '',
        linkType: trailer.linkType || 'NONE',
        linkUrl: trailer.linkUrl || '',
        targetContentId: trailer.targetContentId || '',
        targetPlanId: trailer.targetPlanId || '',
        targetGenreId: trailer.targetGenreId || '',
        isActive: trailer.isActive ?? true,
        isFeatured: trailer.isFeatured ?? false,
        displayOrder: trailer.displayOrder || 0,
        startDate: trailer.startDate ? trailer.startDate.substring(0, 16) : '',
        endDate: trailer.endDate ? trailer.endDate.substring(0, 16) : '',
        targetAudience: trailer.targetAudience || 'ALL',
      });
      setVideoPreview(trailer.videoUrl || null);
      setThumbnailPreview(trailer.thumbnailUrl || null);
      setVideoFile(null);
      setThumbnailFile(null);
    } else if (isOpen) {
      setFormData(createDefaultState());
      setVideoPreview(null);
      setThumbnailPreview(null);
      setVideoFile(null);
      setThumbnailFile(null);
    }
    setErrors({});
  }, [trailer, mode, isOpen]);

  const handleChange = (field: keyof TrailerFormState, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const handleVideoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('video/')) {
      setErrors((prev) => ({
        ...prev,
        video: 'Please select a valid video file',
      }));
      return;
    }

    const maxSizeMb = 500;
    if (file.size > maxSizeMb * 1024 * 1024) {
      setErrors((prev) => ({
        ...prev,
        video: `Video size must be less than ${maxSizeMb}MB`,
      }));
      return;
    }

    setVideoFile(file);
    setErrors((prev) => {
      const next = { ...prev };
      delete next.video;
      return next;
    });

    const reader = new FileReader();
    reader.onload = () => {
      setVideoPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleThumbnailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setErrors((prev) => ({
        ...prev,
        thumbnail: 'Please select a valid image file',
      }));
      return;
    }

    const maxSizeMb = 5;
    if (file.size > maxSizeMb * 1024 * 1024) {
      setErrors((prev) => ({
        ...prev,
        thumbnail: `Thumbnail size must be less than ${maxSizeMb}MB`,
      }));
      return;
    }

    setThumbnailFile(file);
    setErrors((prev) => {
      const next = { ...prev };
      delete next.thumbnail;
      return next;
    });

    const reader = new FileReader();
    reader.onload = () => {
      setThumbnailPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    } else if (formData.title.length > 200) {
      newErrors.title = 'Title must be less than 200 characters';
    }

    if (formData.description && formData.description.length > 1000) {
      newErrors.description = 'Description must be less than 1000 characters';
    }

    if (mode === 'create' && !videoFile && !videoPreview) {
      newErrors.video = 'Video is required for new trailers';
    }

    if (mode === 'create' && !thumbnailFile && !thumbnailPreview) {
      newErrors.thumbnail = 'Thumbnail is required for new trailers';
    }

    if (formData.linkType === 'EXTERNAL' && !formData.linkUrl.trim()) {
      newErrors.linkUrl = 'External URL is required when link type is External';
    }

    if (formData.linkType === 'CONTENT' && !formData.targetContentId.trim()) {
      newErrors.targetContentId = 'Content ID is required when link type is Content';
    }

    if (formData.linkType === 'SUBSCRIPTION_PLAN' && !formData.targetPlanId.trim()) {
      newErrors.targetPlanId = 'Plan ID is required when link type is Subscription Plan';
    }

    if (formData.linkType === 'GENRE' && !formData.targetGenreId.trim()) {
      newErrors.targetGenreId = 'Genre ID is required when link type is Genre';
    }

    if (formData.startDate && formData.endDate) {
      const start = new Date(formData.startDate);
      const end = new Date(formData.endDate);
      if (start >= end) {
        newErrors.endDate = 'End date must be after start date';
      }
    }

    if (formData.displayOrder < 0) {
      newErrors.displayOrder = 'Display order must be 0 or greater';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const payload: TrailerCreateRequest | TrailerUpdateRequest = {
        title: formData.title.trim(),
        description: formData.description.trim() || undefined,
        altText: formData.altText.trim() || undefined,
        linkType: formData.linkType,
        linkUrl: formData.linkType === 'EXTERNAL' ? formData.linkUrl.trim() || null : null,
        targetContentId: formData.linkType === 'CONTENT' ? formData.targetContentId.trim() || null : null,
        targetPlanId: formData.linkType === 'SUBSCRIPTION_PLAN' ? formData.targetPlanId.trim() || null : null,
        targetGenreId: formData.linkType === 'GENRE' ? formData.targetGenreId.trim() || null : null,
        isActive: formData.isActive,
        isFeatured: formData.isFeatured,
        displayOrder: formData.displayOrder,
        startDate: formData.startDate ? new Date(formData.startDate).toISOString() : null,
        endDate: formData.endDate ? new Date(formData.endDate).toISOString() : null,
        targetAudience: formData.targetAudience,
      };

      await onSubmit(payload, videoFile, thumbnailFile);
      handleClose();
    } catch (error) {
      console.error('Trailer form error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setFormData(createDefaultState());
      setErrors({});
      setVideoFile(null);
      setThumbnailFile(null);
      setVideoPreview(null);
      setThumbnailPreview(null);
      onClose();
    }
  };

  const showLinkUrl = formData.linkType === 'EXTERNAL';
  const showTargetContentId = formData.linkType === 'CONTENT';
  const showTargetPlanId = formData.linkType === 'SUBSCRIPTION_PLAN';
  const showTargetGenreId = formData.linkType === 'GENRE';

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={mode === 'create' ? 'Create Trailer' : 'Edit Trailer'}
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Basic Information */}
        <div className="space-y-4">
          <p className="text-2xl font-semibold text-gray-700 text-left">Basic Information</p>

          <FormField
            label="Title"
            value={formData.title}
            onChange={(e) => handleChange('title', e.target.value)}
            error={errors.title}
            required
            placeholder="e.g., New Movie Trailer"
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#EAB308]/30"
              placeholder="Optional description"
            />
            {errors.description && (
              <p className="text-red-500 text-xs mt-1">{errors.description}</p>
            )}
          </div>

          <FormField
            label="Alt Text"
            value={formData.altText}
            onChange={(e) => handleChange('altText', e.target.value)}
            error={errors.altText}
            placeholder="Accessibility text for the thumbnail"
          />
        </div>

        {/* Video Upload */}
        <div className="space-y-4">
          <p className="text-2xl font-semibold text-gray-700 text-left">Trailer Video</p>
          <div className="border border-gray-300 rounded-lg p-4">
            <label className="block text-sm font-medium text-gray-700 mb-1.5 text-left">
              Video {mode === 'create' && <span className="text-red-500">*</span>}
            </label>
            <div className="flex items-center gap-4">
              {videoPreview && (
                <div className="w-32 h-20 rounded bg-gray-100 flex items-center justify-center overflow-hidden border">
                  <video
                    src={videoPreview}
                    className="w-full h-full object-cover"
                    controls={false}
                    muted
                  />
                </div>
              )}
              <div>
                <input
                  type="file"
                  accept="video/*"
                  onChange={handleVideoChange}
                  className="block text-sm text-gray-700"
                />
                <p className="mt-1 text-xs text-gray-500">
                  MP4, MOV, AVI, etc. Max size 500MB.
                </p>
                {errors.video && (
                  <p className="text-sm text-red-600 mt-1.5">{errors.video}</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Thumbnail Upload */}
        <div className="space-y-4">
          <p className="text-2xl font-semibold text-gray-700 text-left">Thumbnail Image</p>
          <div className="border border-gray-300 rounded-lg p-4">
            <label className="block text-sm font-medium text-gray-700 mb-1.5 text-left">
              Thumbnail {mode === 'create' && <span className="text-red-500">*</span>}
            </label>
            <div className="flex items-center gap-4">
              <div className="w-32 h-20 rounded bg-gray-100 flex items-center justify-center overflow-hidden border">
                {thumbnailPreview ? (
                  <img
                    src={thumbnailPreview}
                    alt="Thumbnail preview"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-xs text-gray-400">No image</span>
                )}
              </div>
              <div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleThumbnailChange}
                  className="block text-sm text-gray-700"
                />
                <p className="mt-1 text-xs text-gray-500">
                  JPEG, PNG or WebP. Max size 5MB.
                </p>
                {errors.thumbnail && (
                  <p className="text-sm text-red-600 mt-1.5">{errors.thumbnail}</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Link Configuration */}
        <div className="space-y-4">
          <p className="text-2xl font-semibold text-gray-700 text-left">Link Configuration</p>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Link Type
            </label>
            <select
              value={formData.linkType}
              onChange={(e) => handleChange('linkType', e.target.value as BannerLinkType)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#EAB308]/30"
            >
              {BANNER_LINK_TYPES.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          {showLinkUrl && (
            <FormField
              label="External URL"
              value={formData.linkUrl}
              onChange={(e) => handleChange('linkUrl', e.target.value)}
              error={errors.linkUrl}
              required
              placeholder="https://example.com"
            />
          )}

          {showTargetContentId && (
            <FormField
              label="Content ID"
              value={formData.targetContentId}
              onChange={(e) => handleChange('targetContentId', e.target.value)}
              error={errors.targetContentId}
              required
              placeholder="UUID of the content"
            />
          )}

          {showTargetPlanId && (
            <FormField
              label="Subscription Plan ID"
              value={formData.targetPlanId}
              onChange={(e) => handleChange('targetPlanId', e.target.value)}
              error={errors.targetPlanId}
              required
              placeholder="UUID of the subscription plan"
            />
          )}

          {showTargetGenreId && (
            <FormField
              label="Genre ID"
              value={formData.targetGenreId}
              onChange={(e) => handleChange('targetGenreId', e.target.value)}
              error={errors.targetGenreId}
              required
              placeholder="UUID of the genre"
            />
          )}
        </div>

        {/* Scheduling */}
        <div className="space-y-4">
          <p className="text-2xl font-semibold text-gray-700 text-left">Scheduling</p>

          <div className="grid grid-cols-2 gap-4">
            <FormField
              label="Start Date"
              type="datetime-local"
              value={formData.startDate}
              onChange={(e) => handleChange('startDate', e.target.value)}
              error={errors.startDate}
            />

            <FormField
              label="End Date"
              type="datetime-local"
              value={formData.endDate}
              onChange={(e) => handleChange('endDate', e.target.value)}
              error={errors.endDate}
            />
          </div>
        </div>

        {/* Display Settings */}
        <div className="space-y-4">
          <p className="text-2xl font-semibold text-gray-700 text-left">Display Settings</p>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Target Audience
              </label>
              <select
                value={formData.targetAudience}
                onChange={(e) => handleChange('targetAudience', e.target.value as TargetAudience)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#EAB308]/30"
              >
                {TARGET_AUDIENCES.map((audience) => (
                  <option key={audience.value} value={audience.value}>
                    {audience.label}
                  </option>
                ))}
              </select>
            </div>

            <FormField
              label="Display Order"
              type="number"
              value={formData.displayOrder}
              onChange={(e) => handleChange('displayOrder', parseInt(e.target.value) || 0)}
              error={errors.displayOrder}
              min={0}
            />
          </div>

          <div className="flex items-center gap-4">
            <div className="flex items-center">
              <Checkbox
                checked={formData.isActive}
                onChange={(e) => handleChange('isActive', e.target.checked)}
              />
              <label className="ml-2 text-sm text-gray-700">Active</label>
            </div>

            <div className="flex items-center">
              <Checkbox
                checked={formData.isFeatured}
                onChange={(e) => handleChange('isFeatured', e.target.checked)}
              />
              <label className="ml-2 text-sm text-gray-700">Featured</label>
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end gap-3 pt-4">
          <Button type="button" onClick={handleClose} disabled={isSubmitting} variant="secondary">
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : mode === 'create' ? 'Create Trailer' : 'Update Trailer'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
